import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, Users, Receipt, User, Tag, Clock } from 'lucide-react';
import { useSearchIndex } from '../useSearchIndex';
import { search, addRecent, getRecent, type SearchItem } from '../searchIndex';
import { useDialogA11y } from '@/shared/hooks/useDialogA11y';
import { ATAJO_DE_BUSCAR } from '@/shared/hooks/useGlobalShortcuts';

const KIND_ICON: Record<SearchItem['kind'], typeof Users> = {
  group: Users,
  expense: Receipt,
  member: User,
  tag: Tag,
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SearchPalette({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const { fuse } = useSearchIndex();
  const recent = getRecent();
  // Focus-trap + restauración de foco (Escape lo maneja también onKey abajo).
  const panelRef = useDialogA11y(onClose, open);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Deja al DOM montar antes de enfocar
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const results = query.trim() ? search(fuse, query, 5) : [];
  const showRecent = !query.trim() && recent.length > 0;
  const list: { kind: SearchItem['kind']; id: string; title: string; subtitle?: string | undefined; url: string }[] = showRecent
    ? recent.map((r) => ({ kind: r.kind, id: r.id, title: r.title, subtitle: r.subtitle, url: r.url }))
    : results;

  useEffect(() => {
    setActiveIdx(0);
  }, [query, list.length]);

  const go = (item: { url: string; kind: SearchItem['kind']; id: string; title: string; subtitle?: string | undefined }) => {
    addRecent(item as SearchItem);
    onClose();
    navigate(item.url);
  };

  const onKey = (e: React.KeyboardEvent) => {
    // Escape lo maneja useDialogA11y (a nivel document); aquí solo navegación.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const item = list[activeIdx];
      if (item) go(item);
    }
  };

  if (!open) return null;

  // Layout:
  //  - Desktop (sm+): overlay centrado arriba, input arriba, resultados
  //    debajo (Cmd+K clásico).
  //  - Mobile (<sm): bottom sheet, input pegado abajo cerca del pulgar +
  //    del bottom nav donde vive el botón "Buscar" que abre el palette.
  //    Los resultados aparecen ARRIBA del input para que el teclado no los
  //    tape.
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-start sm:p-4 sm:pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t('search.placeholder', { defaultValue: 'Buscar' })}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="flex w-full max-w-lg flex-col-reverse overflow-hidden rounded-t-xl bg-white shadow-2xl sm:flex-col sm:rounded-xl"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input: en mobile queda ABAJO por el flex-col-reverse; en sm+ queda arriba */}
        <div className="flex items-center gap-2 border-t border-neutral-200 px-3 py-2 sm:border-b sm:border-t-0">
          <Search size={18} className="text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder={t('search.placeholder', { defaultValue: 'Busca grupos, gastos, miembros, tags…' })}
            className="flex-1 border-none bg-transparent text-sm focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-ink"
            aria-label={t('common.close', { defaultValue: 'Cerrar' })}
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {showRecent && (
            <div className="flex items-center gap-1 border-b border-neutral-100 px-3 py-1.5 text-xs uppercase tracking-wider text-muted">
              <Clock size={10} />
              {t('search.recent', { defaultValue: 'Recientes' })}
            </div>
          )}

          {list.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted">
              {query.trim()
                ? t('search.no_results', { defaultValue: 'Sin resultados' })
                : t('search.hint', { defaultValue: 'Escribe para buscar' })}
            </div>
          )}

          <ul>
            {list.map((item, i) => {
              const Icon = KIND_ICON[item.kind];
              return (
                <li key={`${item.kind}:${item.id}`}>
                  <button
                    type="button"
                    onClick={() => go(item)}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                      i === activeIdx ? 'bg-brand-50' : 'hover:bg-neutral-50'
                    }`}
                  >
                    <Icon size={14} className="shrink-0 text-muted" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{item.title}</div>
                      {item.subtitle && <div className="truncate text-xs text-muted">{item.subtitle}</div>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="hidden border-t border-neutral-100 px-3 py-1.5 text-xs text-muted sm:flex sm:items-center sm:justify-between">
          <span>{t('search.hint_keys', { defaultValue: '↑↓ navegar · ↵ abrir · esc cerrar' })}</span>
          <span className="font-mono">{ATAJO_DE_BUSCAR}</span>
        </div>
      </div>
    </div>
  );
}
